/**
 * ACTIVITY-CORE — helpers puros de presentación del feed (agnósticos de UI).
 * Los textos humanos (i18n) viven en la app; aquí solo estructura:
 * parseo del patch, agrupación por batch y detección de "hecho offline".
 */

/** Fila de activity_recent tal y como llega del SQLite local. */
export interface ActivityRow {
  id: string;
  /** = organization_id de la fila de dominio (personal → scope = user_id). */
  scope_id: string | null;
  entity_type: string | null;
  entity_id: string | null;
  action: string | null;
  actor_user_id: string | null;
  actor_name: string | null;
  actor_device_id: string | null;
  platform: string | null;
  server_ts: string | null;
  client_ts: string | null;
  patch: string | null;
  batch_id: string | null;
  reverts_activity_id: string | null;
}

export type ActivityAction = "created" | "updated" | "deleted" | "restored" | "reverted";

export interface ActivityFieldChange {
  old: unknown;
  new: unknown;
}

export type ActivityPatch = Record<string, ActivityFieldChange>;

/** Type guard de un cambio `{old, new}` (sin aserciones inseguras). */
function isFieldChange(value: unknown): value is { old?: unknown; new?: unknown } {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

/** Parsea el patch jsonb (viene como TEXT en SQLite). Nunca lanza. */
export function parseActivityPatch(raw: string | null | undefined): ActivityPatch {
  if (!raw) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    const patch: ActivityPatch = {};
    for (const [field, change] of Object.entries(parsed)) {
      if (isFieldChange(change)) {
        patch[field] = { old: change.old, new: change.new };
      }
    }
    return patch;
  } catch {
    return {};
  }
}

/** Campos cambiados de una entrada, en orden estable. */
export function changedFields(patch: ActivityPatch): string[] {
  return Object.keys(patch).toSorted();
}

/**
 * Umbral a partir del cual una diferencia client_ts↔server_ts se considera
 * "hecho offline y sincronizado después" (y no mera latencia de red).
 */
export const OFFLINE_LAG_THRESHOLD_MS = 60_000;

/**
 * Milisegundos entre el momento en que el cliente hizo el cambio (client_ts)
 * y su llegada al servidor (server_ts). `null` si falta alguno o no parsea.
 */
export function activityLagMs(row: Pick<ActivityRow, "server_ts" | "client_ts">): number | null {
  if (!row.server_ts || !row.client_ts) {
    return null;
  }
  const server = Date.parse(row.server_ts);
  const client = Date.parse(row.client_ts);
  if (Number.isNaN(server) || Number.isNaN(client)) {
    return null;
  }
  return server - client;
}

/** ¿Se hizo offline? (el cliente lo estampó mucho antes de que llegara al servidor) */
export function isOfflineEdit(
  row: Pick<ActivityRow, "server_ts" | "client_ts">,
  thresholdMs: number = OFFLINE_LAG_THRESHOLD_MS,
): boolean {
  const lag = activityLagMs(row);
  return lag !== null && lag > thresholdMs;
}

export interface ActivityBatchGroup<T extends ActivityRow = ActivityRow> {
  /** batch_id común (o el id de la fila si no tiene batch). */
  batchId: string;
  entries: T[];
}

/**
 * Agrupa filas CONSECUTIVAS del feed (ya ordenado server_ts DESC) por batch_id:
 * "recibió 20 líneas" se pinta como un grupo, no 20 tarjetas. No reordena.
 */
export function groupActivityByBatch<T extends ActivityRow>(
  rows: readonly T[],
): ActivityBatchGroup<T>[] {
  const groups: ActivityBatchGroup<T>[] = [];
  for (const row of rows) {
    const key = row.batch_id ?? row.id;
    const last = groups.at(-1);
    if (last && last.batchId === key) {
      last.entries.push(row);
    } else {
      groups.push({ batchId: key, entries: [row] });
    }
  }
  return groups;
}

/**
 * Motivos canónicos de AJUSTE manual de existencias (variante stockpilot §1b:
 * requisito ERP — mermas/robos/correcciones siempre con motivo). El write-path
 * exige `reason` no vacío en TODO move; estos son los valores que ofrece el
 * diálogo de ajuste (el texto humano i18n vive en la app).
 */
export const ADJUSTMENT_REASONS = ["merma", "rotura", "robo", "recuento", "otro"] as const;

export type AdjustmentReason = (typeof ADJUSTMENT_REASONS)[number];

export function isAdjustmentReason(value: string | null | undefined): value is AdjustmentReason {
  return Boolean(value) && (ADJUSTMENT_REASONS as readonly string[]).includes(value ?? "");
}
